/**
 * CodingWiz run options. The team's saved defaults live on the organization
 * (`api/coding_wiz_preferences.ts`); this module holds the shape and the pure helpers.
 */
export interface CodingWizPreferences {
  split_into_multiple_lines: boolean
  split_by_retailer: boolean
  split_by_product_line: boolean
  split_by_execution_date: boolean
  split_by_admin_fee: boolean
}

export type CodingWizRunMode = "ledger" | "split"

const DEFAULT_PREFERENCES: CodingWizPreferences = {
  split_into_multiple_lines: false,
  split_by_retailer: true,
  split_by_product_line: false,
  split_by_execution_date: false,
  split_by_admin_fee: false,
}

export function getDefaultCodingWizPreferences(): CodingWizPreferences {
  return { ...DEFAULT_PREFERENCES }
}

export function coding_wiz_run_mode(prefs: CodingWizPreferences): CodingWizRunMode {
  return prefs.split_into_multiple_lines ? "split" : "ledger"
}

export function coding_wiz_split_grouping_valid(prefs: CodingWizPreferences): boolean {
  if (!prefs.split_into_multiple_lines) {
    return true
  }
  return (
    prefs.split_by_retailer ||
    prefs.split_by_product_line ||
    prefs.split_by_execution_date ||
    prefs.split_by_admin_fee
  )
}

export function build_split_grouping_instructions(prefs: CodingWizPreferences): string | null {
  if (!prefs.split_into_multiple_lines) {
    return null
  }
  const dimensions: string[] = []
  if (prefs.split_by_retailer) {
    dimensions.push("retailer")
  }
  if (prefs.split_by_product_line) {
    dimensions.push("product line")
  }
  if (prefs.split_by_execution_date) {
    dimensions.push("execution date")
  }
  if (prefs.split_by_admin_fee) {
    dimensions.push("admin fee")
  }
  if (dimensions.length === 0) {
    return null
  }
  return `Prefer splitting by: ${dimensions.join(", ")}.`
}

export function resolve_coding_wiz_feedback(
  prefs: CodingWizPreferences,
  user_instructions?: string | null
): string | null {
  const trimmed = user_instructions?.trim() ?? ""
  if (!prefs.split_into_multiple_lines) {
    return trimmed || null
  }
  const grouping = build_split_grouping_instructions(prefs)
  if (trimmed && grouping) {
    return `${trimmed}\n\n${grouping}`
  }
  return trimmed || grouping || null
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("coding_wiz_split_grouping_valid", () => {
    it("is true for single-line mode without grouping", () => {
      expect(
        coding_wiz_split_grouping_valid({
          split_into_multiple_lines: false,
          split_by_retailer: false,
          split_by_product_line: false,
          split_by_execution_date: false,
          split_by_admin_fee: false,
        })
      ).toBe(true)
    })

    it("requires at least one dimension when splitting", () => {
      expect(
        coding_wiz_split_grouping_valid({
          split_into_multiple_lines: true,
          split_by_retailer: false,
          split_by_product_line: false,
          split_by_execution_date: false,
          split_by_admin_fee: false,
        })
      ).toBe(false)
    })
  })

  describe("build_split_grouping_instructions", () => {
    it("returns null for single-line mode", () => {
      expect(build_split_grouping_instructions(getDefaultCodingWizPreferences())).toBe(null)
    })

    it("lists selected dimensions", () => {
      const text = build_split_grouping_instructions({
        split_into_multiple_lines: true,
        split_by_retailer: true,
        split_by_product_line: true,
        split_by_execution_date: false,
        split_by_admin_fee: false,
      })
      expect(text).toBe("Prefer splitting by: retailer, product line.")
    })
  })

  describe("resolve_coding_wiz_feedback", () => {
    it("returns user text only for ledger mode", () => {
      expect(resolve_coding_wiz_feedback(getDefaultCodingWizPreferences(), "  use sprouts  ")).toBe("use sprouts")
    })

    it("merges instructions and grouping for split mode", () => {
      const prefs: CodingWizPreferences = {
        split_into_multiple_lines: true,
        split_by_retailer: true,
        split_by_product_line: false,
        split_by_execution_date: false,
        split_by_admin_fee: false,
      }
      expect(resolve_coding_wiz_feedback(prefs, "focus on Q1")).toBe(
        "focus on Q1\n\nPrefer splitting by: retailer."
      )
    })
  })
}
